import { FC, useState, useEffect, useMemo } from 'react';
import { Spinner } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getCollectionsList, getCollectionReturns } from '../../../apis/backend';
import CollectionCard from '../../CollectionCard';
import { colors, spacing, fontStyles } from '../../../styles';
import ErrorSection from '../../ErrorSection';
import { getCollectionImages } from '../../../utils/getImages';
import Button from '../../Button';
import * as stores from '../../../stores';
import { FREE_COLLECTIONS_LIMIT } from '../../../constants/freemium';
import { ORDERED_SECTIONS } from '../../../utils/collectionTranslations';

const styles = {
  section: css`
    margin-bottom: ${spacing.deci};
  `,
  sectionTitle: css`
    ${fontStyles.lion};
    color: ${colors.liquorice};
    margin-bottom: ${spacing.milli};
    html[data-theme='dark'] & {
      color: ${colors.tungsten};
    }
  `,
  horizontalList: css`
    display: flex;
    overflow-x: auto;
    gap: ${spacing.deci};
    padding: ${spacing.centi} 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  item: css`
    flex: 0 0 auto;
    width: 140px;
    position: relative;

    &::before {
      content: '';
      padding-bottom: 120%;
      display: block;
    }
  `,
  itemContent: css`
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    text-align: center;
  `,
  cardWrapper: css`
    width: 100%;
    height: 100%;
  `,
};

// Section keys extracted to collectionTranslations.ts for i18n support
const SECTION_API_KEYS = ORDERED_SECTIONS.map((s) => s.apiKey);

interface Props {
  selectedTimeRange: string;
}

const CollectionList: FC<Props> = ({ selectedTimeRange }) => {
  const { t } = useTranslation();
  const query = useQuery(['investments', 'collections'], async () => getCollectionsList());
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const [imageMap, setImageMap] = useState<Record<string, string>>({});
  const [collectionsBySection, setCollectionsBySection] = useState<Record<string, any[]>>({});

  // Stabilize subscription status to prevent unnecessary re-renders
  const hasActiveSubscription = useMemo(() => {
    return !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;
  }, [purchaserInfo?.entitlements?.active]);

  // Pre-compute a flat list of all collections with per-section indices for paywall.
  // Free users see the first FREE_COLLECTIONS_LIMIT items within each section/row.
  // Only non-restricted collections consume free-tier index slots; restricted
  // collections are excluded from the index map so they are always locked via
  // the isRestricted check regardless of position.
  const allCollectionsWithIndex = useMemo(() => {
    if (!query.data) return [];
    const result: Array<{ collection: any; sectionIndex: number }> = [];
    SECTION_API_KEYS.forEach((sectionKey) => {
      const sectionCollections = query.data.filter((c) => c.section === sectionKey);
      let sectionIndex = 0;
      sectionCollections.forEach((collection) => {
        if (!collection.restrictedToSubscribers) {
          result.push({ collection, sectionIndex });
          sectionIndex++;
        }
        // Restricted collections are omitted from the index map; their
        // collectionIndexMap lookup returns undefined → Infinity → always locked.
      });
    });
    return result;
  }, [query.data]);

  // Create a lookup map from collection id to its per-section index
  const collectionIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    allCollectionsWithIndex.forEach(({ collection, sectionIndex }) => {
      map.set(collection.id, sectionIndex);
    });
    return map;
  }, [allCollectionsWithIndex]);

  // Get collection IDs for the returns query - only include collections user has access to.
  // Subscribers get all collections (including restricted ones); free users get only
  // non-restricted collections within the free limit.
  const collectionIds = useMemo(() => {
    if (!query.data) return [];
    if (hasActiveSubscription) {
      return query.data.map((c) => c.id);
    }
    return allCollectionsWithIndex
      .filter(({ sectionIndex }) => sectionIndex < FREE_COLLECTIONS_LIMIT)
      .map(({ collection }) => collection.id);
  }, [query.data, allCollectionsWithIndex, hasActiveSubscription]);

  // Fetch returns for all collections for the selected time range
  const returnsQuery = useQuery(
    ['collection-returns', selectedTimeRange, collectionIds],
    async () => getCollectionReturns(collectionIds, selectedTimeRange),
    {
      enabled: collectionIds.length > 0,
      staleTime: 60000, // 1 minute
      retry: 2, // Retry failed requests up to 2 times
      onError: (err) => {
        console.warn('Failed to fetch collection returns:', err);
        // Silently fail - returns will show as undefined and won't display
      },
    },
  );

  useEffect(() => {
    const updateImages = () => {
      const newImageMap: Record<string, string> = {};
      query.data?.forEach((collection) => {
        const imageSrc = getCollectionImages(collection.imageId);
        if (imageSrc && collection.imageId) {
          newImageMap[collection.imageId] = imageSrc;
        }
      });
      setImageMap(newImageMap);
    };

    updateImages();

    window.addEventListener('themeChanged', updateImages);
    return () => window.removeEventListener('themeChanged', updateImages);
  }, [query.data]);

  useEffect(() => {
    if (query.data) {
      // Group collections by section
      const grouped = query.data.reduce(
        (acc, collection) => {
          const section = collection.section || 'OTHER';
          if (!acc[section]) {
            acc[section] = [];
          }
          acc[section].push(collection);
          return acc;
        },
        {} as Record<string, any[]>,
      );

      setCollectionsBySection(grouped);
    }
  }, [query.data]);

  const renderCollectionCard = (collection: any) => {
    const imageSrc = imageMap[collection.imageId ?? ''];

    // Get the per-section index for this collection (position within its row)
    const sectionIndex = collectionIndexMap.get(collection.id) ?? Infinity;

    // Paywall: locked if beyond free limit OR restricted (unless subscribed).
    // FREE_COLLECTIONS_LIMIT applies per section/row — free users see the first N
    // collections in each row, not the first N globally.
    // Treat restricted collections as locked while purchase info is still loading to
    // prevent a brief unlocked flash before subscription status is confirmed.
    const isRestricted = collection.restrictedToSubscribers && (!isPurchaseInfoLoaded || !hasActiveSubscription);
    const isBeyondFreeLimit = !hasActiveSubscription && sectionIndex >= FREE_COLLECTIONS_LIMIT;
    const isLocked = isRestricted || isBeyondFreeLimit;

    // Get the return value from the returns query
    const returnValue = returnsQuery.data?.[collection.id] ?? undefined;

    const cardComponent = (
      <CollectionCard
        name={t(`collections.names.${collection.slug}`, { defaultValue: collection.name })}
        imageSrc={imageSrc}
        isLocked={isLocked}
        returnValue={returnValue}
      />
    );

    return (
      <div key={collection.id} css={styles.item}>
        <div css={styles.itemContent}>
          <div css={styles.cardWrapper}>
            {isLocked ? (
              <Button
                onClick={() => stores.showPaymentModalByPreference('collections_page')}
                css={css`
                  height: 100%;
                  width: 100%;
                `}
              >
                {cardComponent}
              </Button>
            ) : (
              <Link to={`/collection/${collection.id}`}>{cardComponent}</Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (query.isLoading)
    return (
      <div
        css={css`
          text-align: center;
          margin-top: ${spacing.deka};
        `}
      >
        <Spinner color={colors.cloud} size="lg" />
      </div>
    );

  if (query.isError || !query.data)
    return (
      <div
        css={css`
          margin-top: ${spacing.deka};
        `}
      >
        <ErrorSection text={t('collections.fetchError')} onRetry={() => query.refetch()} />
      </div>
    );

  return (
    <div>
      {ORDERED_SECTIONS.map(({ apiKey, titleKey }) => {
        const sectionCollections = collectionsBySection[apiKey] || [];
        if (sectionCollections.length === 0) return null;

        return (
          <section key={apiKey} css={styles.section}>
            <h2 css={styles.sectionTitle}>{t(titleKey)}</h2>
            <div css={styles.horizontalList}>
              {sectionCollections.map((collection) => renderCollectionCard(collection))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default CollectionList;
